<template>
  <NotificationComponent
    data-testid="validation-errors"
    id="pnlErrorMessages"
    class="mb-8"
    heading-text="We still need some more information from you before you continue"
    :variant="variant"
  >
    <template #body>
      <ul class="list-disc pl-14">
        <li
          v-for="(ruleKey, index) in Object.keys(failedRules)"
          :id="ruleKey"
          :key="index"
          :data-testid="ruleKey"
        >
          <template v-if="variant === 'error'">
            <span v-html="addClickableLinks(failedRules[ruleKey], ruleKey)"></span>
          </template>
          <template v-else>
            {{ stripHtml(failedRules[ruleKey]) }}
          </template>
        </li>
      </ul>
    </template>
  </NotificationComponent>
</template>

<script setup lang="ts">
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import stripHtml from '@/utils/stripHtml'
import limits from '../../constants/limits'

defineProps({
  failedRules: { required: true, type: Object },
  variant: { type: String, default: 'warning' },
})

// Map validation rules to their target IDs and link text
const validationRuleMap: Record<string, { targetId: string, linkText: string }> = {
  'at-least-one-author-required': { targetId: 'authorInformationTitle', linkText: 'author' },
  'manuscript-file-required': { targetId: 'required-manuscript', linkText: 'manuscript file' },
  'cover-letter-required': { targetId: 'required-cover-letter', linkText: 'cover letter' },
  'themed-collection-option-required': { targetId: 'themed-collection-option-required', linkText: 'themed collection' },
  'data-availability-statement-option-required': { targetId: 'das-required', linkText: 'Data Availability Statement' },
  'article-title-required': { targetId: 'article-title-required', linkText: 'article title' },
  'article-abstract-required': { targetId: 'article-abstract-required', linkText: 'abstract' },
  'at-least-two-recommended-reviewers-required': { targetId: 'recommendedReviewersTitle', linkText: 'recommended reviewers' },
  'anonymised-peer-review-option-required': { targetId: 'reviewTypeTitle', linkText: 'single or double anonymised peer review' },
  'at-least-two-associate-editors-required': { targetId: '2-associate-editors', linkText: 'Associate Editors' },
  'ccdc-deposition-number-required': { targetId: 'ccdcd-required', linkText: 'CCDC deposition number' },
  'terms-and-conditions-required': { targetId: 'terms-and-conditions', linkText: 'Terms and Conditions' },
  'transparent-peer-review-option-required': { targetId: 'titleTPR', linkText: 'transparent peer review' },
  'all-authors-must-be-valid': { targetId: 'authorInformationTitle', linkText: 'All authors must be valid' },
  [`max-words:${limits.manuscript.words.title},title`]: { targetId: 'article-title-required', linkText: `not exceed ${limits.manuscript.words.title} words` },
  [`max-words:${limits.manuscript.words.abstract},abstract`]: { targetId: 'article-abstract-required', linkText: `not exceed ${limits.manuscript.words.abstract} words` },
  [`max-characters-length:${limits.themedCollection.characters.title},Themed collection title must not exceed`]: { targetId: 'themed-collection-option-required', linkText: `not exceed ${limits.themedCollection.characters.title} characters` },
  [`max-characters-length:${limits.DAS.characters.statement},Data Availability Statement must not exceed`]: { targetId: 'das-required', linkText: `not exceed ${limits.DAS.characters.statement} characters` },
}

const addClickableLinks = (message: string, ruleKey: string): string => {
  const maxCharsMatch = message.match(/not exceed \d+ characters/)
  if (maxCharsMatch) {
    const mapping
      = validationRuleMap[ruleKey]
        || Object.entries(validationRuleMap).find(
          ([key]) => key.startsWith('max-characters-length'),
        )?.[1]

    if (mapping) {
      const link = `<a data-target-id="${mapping.targetId}" class="scroll-link underline cursor-pointer">${maxCharsMatch[0]}</a>`
      const finalMsg = message.replace(maxCharsMatch[0], link)
      return finalMsg
    }
    return message
  }

  const mapping = validationRuleMap[ruleKey]
  if (!mapping) {
    return message
  }

  const link = `<a data-target-id="${mapping.targetId}" class="scroll-link underline cursor-pointer">${mapping.linkText}</a>`
  const finalMsg = message.replace(mapping.linkText, link)
  return finalMsg
}

</script>
